import { useState } from 'react';
import { api, type DriverLink, type KycEvidence, type NameAgreement, type Role } from '../api';
import { Banner, Card, Empty, Loading, Modal, Status, useAsync, when } from '../ui';

const KYC_TONE: Record<string, string> = {
  verified: 'ok', failed: 'danger', in_progress: 'info', pending: 'warn', unverified: '',
};

const LINK_LABEL: Record<DriverLink['kind'], string> = {
  bvn: 'Same BVN',
  phone: 'Same phone number',
  payout_account: 'Same payout account',
  device: 'Same device',
};

/**
 * One driver, in full.
 *
 * Three questions, in the order a reviewer asks them: who is this person, is
 * the evidence good, and who are they connected to.
 */
export function DriverDetail({ driverRef, role, back, openReferral, openDriver }: {
  driverRef: string;
  role: Role;
  back: () => void;
  openReferral: (id: string) => void;
  openDriver: (ref: string) => void;
}) {
  const { data, error, loading } = useAsync(() => api.driver(driverRef), [driverRef]);
  const [reveal, setReveal] = useState<{ field: 'bvn' | 'nin' | 'phone' } | null>(null);
  const [reason, setReason] = useState('');
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  if (loading) return <Loading label="Loading driver" />;
  if (error) return <Banner tone="danger" title="Could not load this driver">{error}</Banner>;
  if (!data) return null;

  const canReveal = role === 'zeeh_admin';
  const hardLinks = data.linked_drivers.filter((l) => l.severity === 'hard');

  async function doReveal() {
    if (!reveal || reason.trim().length < 10) return;
    setBusy(true);
    try {
      const r = await api.reveal(driverRef, reveal.field, reason.trim());
      setRevealed((prev) => ({ ...prev, [reveal.field]: r.value }));
      setNote({ tone: 'ok', text: `${reveal.field.toUpperCase()} revealed. This access is recorded against your account.` });
      setReveal(null);
      setReason('');
    } catch (e) {
      setNote({ tone: 'danger', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const IdentityRow = ({ field, label, masked }: { field: 'bvn' | 'nin' | 'phone'; label: string; masked: string | null }) => (
    <>
      <dt>{label}</dt>
      <dd className="row" style={{ gap: 8 }}>
        <span className="mono">{revealed[field] ?? masked ?? '—'}</span>
        {masked && canReveal && !revealed[field] && (
          <button className="btn sm ghost" onClick={() => setReveal({ field })}>Reveal</button>
        )}
        {revealed[field] && <span className="pill warn">recorded</span>}
      </dd>
    </>
  );

  return (
    <div className="stack">
      <div className="row">
        <button className="btn ghost sm" onClick={back}>← All drivers</button>
      </div>

      {note && <Banner tone={note.tone}>{note.text}</Banner>}

      {hardLinks.length > 0 && (
        <Banner tone="danger" title="Shares an identity with another driver">
          {/* Several links of the same kind describe one fact, not several. */}
          This driver shares {[...new Set(hardLinks.map((l) => LINK_LABEL[l.kind].toLowerCase()))].join(' and ')} with{' '}
          {new Set(hardLinks.map((l) => l.driver_ref)).size} other driver{' '}
          {new Set(hardLinks.map((l) => l.driver_ref)).size === 1 ? 'account' : 'accounts'}. A referral between any
          of them is a self-referral and is rejected automatically.
        </Banner>
      )}

      <div className="detail-grid">
        <div className="stack">
          <Card title="Identity">
            <div className="row" style={{ alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600 }}>
                {data.verified_name ?? 'Not yet verified'}
              </div>
              <span className={`pill ${KYC_TONE[data.kyc_status] ?? ''}`}>{data.kyc_status.replace(/_/g, ' ')}</span>
            </div>
            <dl className="dl">
              <dt>inDrive driver id</dt><dd className="mono">{data.driver_id}</dd>
              <IdentityRow field="bvn" label="BVN" masked={data.bvn_masked} />
              <IdentityRow field="nin" label="NIN" masked={data.nin_masked} />
              <IdentityRow field="phone" label="Phone (from inDrive)" masked={data.phone_masked} />
              <dt>Phone (from driver)</dt>
              <dd>
                {data.self_phone_masked
                  ? <span className="mono">{data.self_phone_masked}</span>
                  : <span className="muted">not yet given</span>}
              </dd>
              <dt>First seen</dt><dd>{when(data.first_seen)}</dd>
              <dt>Last activity</dt><dd>{when(data.last_activity)}</dd>
            </dl>
            {!canReveal && (data.bvn_masked || data.nin_masked) && (
              <p className="small muted" style={{ margin: '14px 0 0', lineHeight: 1.6 }}>
                Identifiers are stored encrypted and shown masked. A Zeeh administrator can reveal one in full
                with a written reason, which is recorded permanently against their account.
              </p>
            )}
          </Card>

          <Card title="KYC evidence" flush>
            {data.kyc_evidence ? <Evidence e={data.kyc_evidence} /> : (
              <div style={{ padding: 18 }} className="muted small">No KYC has been run for this driver yet.</div>
            )}
          </Card>

          <Card title={`Drivers recruited (${data.recruited.length})`} flush>
            {data.recruited.length === 0 ? (
              <Empty title="Has not referred anyone" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Recruited driver</th><th>Status</th>
                      <th className="num">Commission</th><th>Paid to</th><th className="num">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recruited.map((m) => (
                      <tr key={m.referral_id} className="clickable" onClick={() => openReferral(m.referral_id)}>
                        <td>
                          <div>{m.referred_name ?? <span className="muted">unverified</span>}</div>
                          <div className="small muted mono">{m.referred_driver_id}</div>
                        </td>
                        <td><Status value={m.status} /></td>
                        <td className="num">{m.amount_display}</td>
                        <td className="small mono muted">{m.destination_masked}</td>
                        <td className="num small muted">{when(m.created_at, { time: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="stack">
          <Card title="Programme value">
            <dl className="dl">
              <dt>Drivers recruited</dt><dd>{data.referrals_made}</dd>
              <dt>Paid</dt><dd>{data.referrals_paid}</dd>
              <dt>Flagged</dt>
              <dd style={data.referrals_flagged > 0 ? { color: 'var(--warn)', fontWeight: 600 } : undefined}>
                {data.referrals_flagged}
              </dd>
              <dt>Rejected</dt><dd>{data.referrals_rejected}</dd>
              <dt>Total earned</dt><dd style={{ fontWeight: 600 }}>{data.total_earned_display}</dd>
              <dt>Awaiting payout</dt>
              <dd>{BigInt(data.pending_value) > 0n ? data.pending_value_display : <span className="muted">—</span>}</dd>
            </dl>
          </Card>

          <Card title="Recruited by">
            {data.recruited_by ? (
              <>
                <button
                  className="btn ghost sm"
                  style={{ padding: 0, height: 'auto', fontWeight: 600 }}
                  onClick={() => openDriver(data.recruited_by!.driver_ref)}
                >
                  {data.recruited_by.verified_name ?? data.recruited_by.driver_id} →
                </button>
                <dl className="dl" style={{ marginTop: 12 }}>
                  <dt>Driver id</dt><dd className="mono small">{data.recruited_by.driver_id}</dd>
                  <dt>That referral</dt><dd><Status value={data.recruited_by.status} /></dd>
                  <dt>Commission</dt><dd>{data.recruited_by.amount_display}</dd>
                  <dt>When</dt><dd>{when(data.recruited_by.at, { time: false })}</dd>
                </dl>
              </>
            ) : (
              <p className="small muted" style={{ margin: 0 }}>
                Joined independently — no one claimed a commission for this driver.
              </p>
            )}
          </Card>

          <Card title={`Connected drivers (${data.linked_drivers.length})`} flush>
            {data.linked_drivers.length === 0 ? (
              <div style={{ padding: 18 }} className="muted small">
                No shared identity, account or device with any other driver.
              </div>
            ) : (
              <div style={{ padding: '4px 0' }}>
                {data.linked_drivers.map((l) => (
                  <div className="check-row" key={`${l.driver_ref}-${l.kind}`}>
                    <span className={`tick ${l.severity === 'hard' ? 'fail' : 'na'}`}>
                      {l.severity === 'hard' ? '!' : '·'}
                    </span>
                    <span className="name">
                      <button
                        className="btn ghost sm"
                        style={{ padding: 0, height: 'auto', fontWeight: 550 }}
                        onClick={() => openDriver(l.driver_ref)}
                      >
                        {l.verified_name ?? l.driver_id}
                      </button>
                      <div className="small muted">{LINK_LABEL[l.kind]}</div>
                    </span>
                    <span className="small muted">{l.occurrences > 1 ? `×${l.occurrences}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {reveal && (
        <Modal
          title={`Reveal this driver's ${reveal.field.toUpperCase()}`}
          description="Unmasked identity data. Your name, the field and your reason are written to the audit log permanently."
          onClose={() => { setReveal(null); setReason(''); }}
          footer={
            <>
              <button className="btn" onClick={() => { setReveal(null); setReason(''); }}>Cancel</button>
              <button className="btn danger" disabled={busy || reason.trim().length < 10} onClick={doReveal}>
                {busy ? <span className="spin" /> : 'Reveal and record'}
              </button>
            </>
          }
        >
          <dl className="dl">
            <dt>Driver</dt><dd>{data.verified_name ?? data.driver_id}</dd>
            <dt>Field</dt><dd>{reveal.field.toUpperCase()}</dd>
          </dl>
          <div className="field">
            <label htmlFor="reveal-reason">Why do you need this?</label>
            <textarea
              id="reveal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Reviewing flagged referral 3a91c2 — confirming the BVN against the document the driver submitted to support."
            />
            <span className="hint">At least 10 characters. The value itself is never logged — only that you looked.</span>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** How closely two sources agreed on a name. */
function Agreement({ label, a }: { label: string; a: NameAgreement | null }) {
  if (!a) return (
    <div className="check-row">
      <span className="tick na">·</span>
      <span className="name">{label}</span>
      <span className="small muted">not comparable</span>
    </div>
  );
  return (
    <div className="check-row">
      <span className={`tick ${a.matched ? 'pass' : a.borderline ? 'na' : 'fail'}`}>
        {a.matched ? '✓' : a.borderline ? '?' : '✕'}
      </span>
      <span className="name">
        {label}
        <div className="small muted">
          {a.matched ? 'the same person' : a.borderline ? 'close, but not conclusive — needs a human' : 'different people'}
        </div>
      </span>
      <span className="small muted num">
        {a.score.toFixed(2)} avg · {a.weakest.toFixed(2)} worst word
      </span>
    </div>
  );
}

function Evidence({ e }: { e: KycEvidence }) {
  const { sources: s, screening } = e;
  return (
    <>
      <Section>What each source returned</Section>
      <SourceRow label="BVN" status={s.bvn?.status} name={s.bvn?.name} dob={s.bvn?.date_of_birth} providerRef={s.bvn?.provider_reference} />
      <SourceRow label="NIN" status={s.nin?.status} name={s.nin?.name} dob={s.nin?.date_of_birth} providerRef={s.nin?.provider_reference} />
      <SourceRow label="Bank account" status={s.bank_account?.status} name={s.bank_account?.account_name} sub={s.bank_account?.bank_name} providerRef={s.bank_account?.provider_reference} />

      <Section>Do they describe the same person?</Section>
      <Agreement label="BVN name vs NIN name" a={e.agreement.bvn_vs_nin} />
      <Agreement label="Verified identity vs bank account holder" a={e.agreement.identity_vs_account} />

      <Section>Biometrics</Section>
      {s.biometrics ? (
        <>
          <div className="check-row">
            <span className={`tick ${s.biometrics.is_live ? 'pass' : 'fail'}`}>{s.biometrics.is_live ? '✓' : '✕'}</span>
            <span className="name">Liveness</span>
            <span className="small muted">{s.biometrics.is_live ? 'live capture' : 'failed'}</span>
          </div>
          <div className="check-row">
            <span className={`tick ${(s.biometrics.match_score ?? 0) >= 0.8 ? 'pass' : 'fail'}`}>
              {(s.biometrics.match_score ?? 0) >= 0.8 ? '✓' : '✕'}
            </span>
            <span className="name">Selfie matches BVN photograph</span>
            <span className="small muted num">{s.biometrics.match_score?.toFixed(2) ?? '—'}</span>
          </div>
        </>
      ) : (
        <div className="check-row">
          <span className="tick na">·</span>
          <span className="name">Liveness &amp; face match</span>
          <span className="small muted">no selfie collected</span>
        </div>
      )}

      <Section>Screening</Section>
      <ScreenRow label="Sanctions" hit={screening.sanctions_hit} lists={screening.matched_lists} />
      <ScreenRow label="Terrorist watchlist" hit={screening.terrorist_hit} />
      <ScreenRow label="Politically exposed person" hit={screening.pep_hit} note="requires enhanced diligence" />
      <div className="check-row">
        <span className={`tick ${(screening.aml_score ?? 0) < 70 ? 'pass' : 'fail'}`}>
          {(screening.aml_score ?? 0) < 70 ? '✓' : '✕'}
        </span>
        <span className="name">AML risk score</span>
        <span className="small muted num">{screening.aml_score ?? '—'} / 100</span>
      </div>

      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--rule)' }} className="small muted">
        Checked {when(e.checked_at)} · decision <strong>{e.decision}</strong>
        {e.reason_codes.length > 0 && (
          <div style={{ marginTop: 6 }}>{e.reason_codes.map((c) => <span className="code-chip" key={c}>{c}</span>)}</div>
        )}
      </div>
    </>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '11px 18px 7px', fontSize: 10.5, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: 'var(--ink-3)', fontWeight: 600,
      borderBottom: '1px solid var(--rule)', background: 'var(--surface-sunk)',
    }}>
      {children}
    </div>
  );
}

function SourceRow({ label, status, name, dob, sub, providerRef }: {
  label: string;
  status?: string;
  name?: string | null;
  dob?: string | null;
  sub?: string | null;
  // Deliberately not called `ref`: React reserves that name, and passing it as
  // an ordinary prop throws at render time while type-checking perfectly.
  providerRef?: string | null;
}) {
  const ok = status === 'found' || status === 'resolved';
  return (
    <div className="check-row">
      <span className={`tick ${status ? (ok ? 'pass' : 'fail') : 'na'}`}>{status ? (ok ? '✓' : '✕') : '·'}</span>
      <span className="name">
        <strong style={{ fontWeight: 550 }}>{label}</strong>{' '}
        {name ? <span>{name}</span> : <span className="muted">{status ?? 'not checked'}</span>}
        <div className="small muted">
          {dob && `born ${dob}`}{dob && sub ? ' · ' : ''}{sub}
          {providerRef && <span className="mono"> {dob || sub ? '· ' : ''}{providerRef}</span>}
        </div>
      </span>
    </div>
  );
}

function ScreenRow({ label, hit, lists, note }: { label: string; hit: boolean | null; lists?: string[]; note?: string }) {
  return (
    <div className="check-row">
      <span className={`tick ${hit === null ? 'na' : hit ? 'fail' : 'pass'}`}>{hit === null ? '·' : hit ? '✕' : '✓'}</span>
      <span className="name">{label}</span>
      <span className="small muted">
        {hit === null ? 'not screened' : hit ? (lists?.length ? lists.join(', ') : (note ?? 'match found')) : 'clear'}
      </span>
    </div>
  );
}
